import { useEffect, useRef, useState, type Dispatch, type SetStateAction } from "react";

export type AdminView = "submissions" | "invites" | "formConfig" | "categoryTaxonomy";

const ADMIN_VIEW_PATHS: Record<AdminView, string> = {
  submissions: "/admin",
  invites: "/admin/invites",
  formConfig: "/admin/form-config",
  categoryTaxonomy: "/admin/categories",
};

export function getAdminViewPath(view: AdminView): string {
  return ADMIN_VIEW_PATHS[view];
}

export function getAdminViewFromPathname(pathname: string): AdminView {
  const normalized = pathname.replace(/\/+$/, "");
  const matched = (Object.keys(ADMIN_VIEW_PATHS) as AdminView[]).find((view) => ADMIN_VIEW_PATHS[view] === normalized);
  return matched ?? "submissions";
}

export function applyAdminSearchParams(href: string, patch: Record<string, string | null>): string {
  const url = new URL(href, "http://admin.local");
  for (const [key, value] of Object.entries(patch)) {
    if (value) url.searchParams.set(key, value);
    else url.searchParams.delete(key);
  }
  return `${url.pathname}${url.search}`;
}

// 화면 이동 시에도 현재 쿼리를 유지한다. 새 화면에 해당하지 않는 파라미터는 각 화면의 동기화 효과가 정리한다.
// 같은 메뉴를 다시 눌렀을 때처럼 URL이 그대로면 히스토리에 쌓지 않는다.
export function pushAdminPath(path: string): void {
  const next = `${path}${window.location.search}`;
  if (next === `${window.location.pathname}${window.location.search}`) return;
  window.history.pushState(null, "", next);
}

function readAdminSearchParam(key: string): string | null {
  if (typeof window === "undefined") return null;
  return new URLSearchParams(window.location.search).get(key);
}

function replaceAdminSearchParams(patch: Record<string, string | null>): void {
  if (typeof window === "undefined") return;
  const next = applyAdminSearchParams(window.location.href, patch);
  if (next === `${window.location.pathname}${window.location.search}`) return;
  window.history.replaceState(null, "", next);
}

// 화면에서 고른 탭/필터를 쿼리스트링에 반영해 현재 URL을 그대로 공유할 수 있게 한다.
export function useAdminSearchParam<T extends string = string>(
  key: string,
  initial: NoInfer<T> | (() => NoInfer<T>),
  active = true,
): [T, Dispatch<SetStateAction<T>>] {
  const fallbackRef = useRef<T | null>(null);
  if (fallbackRef.current === null) fallbackRef.current = typeof initial === "function" ? initial() : initial;
  const readValue = () => (readAdminSearchParam(key) as T | null) ?? (fallbackRef.current as T);
  const [value, setValue] = useState<T>(readValue);

  useEffect(() => {
    replaceAdminSearchParams({ [key]: active ? value : null });
  }, [key, value, active]);

  // 뒤로/앞으로 가기로 URL이 바뀌면 메모리 값이 아니라 복원된 URL을 기준으로 삼는다.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const syncFromUrl = () => setValue(readValue());
    window.addEventListener("popstate", syncFromUrl);
    return () => window.removeEventListener("popstate", syncFromUrl);
  }, [key]);

  useEffect(() => () => replaceAdminSearchParams({ [key]: null }), [key]);

  return [value, setValue];
}

export function getAdminSubmissionPath(id: string): string {
  return `/admin/submissions/${encodeURIComponent(id)}`;
}

export function getAdminSubmissionIdFromPathname(pathname: string): string | null {
  const match = pathname.match(/^\/admin\/submissions\/([^/]+)\/?$/);
  if (!match?.[1]) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return null;
  }
}

export function getReadyAdminSubmissionId(input: {
  selectedId: string | null;
  loadedId: string | null;
  isLoading: boolean;
}): string | null {
  if (input.isLoading || !input.selectedId || input.loadedId !== input.selectedId) return null;
  return input.selectedId;
}
